<template>
  <div>
    <!-- 1) Выбор часового пояса -->
    <div>
      <select
          v-model="selectedTimeZone"
          @change="setTimeZone"
          class="my-5 float-right bg-gray-50 border border-gray-300 text-gray-900
               text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500
               block p-2.5 dark:bg-gray-700 dark:border-gray-600
               dark:placeholder-gray-400 dark:text-white
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
      >
        <option value="Asia/Shanghai">Asia/Shanghai</option>
        <option value="Europe/Kiev">Europe/Kiev</option>
      </select>
    </div>
    <div class="clear-both"></div>

    <!-- 2) Выбор даты (диапазона) -->
    <div class="date-picker">
      <VueDatepicker
          v-model="date"
          range
          :enableTimePicker="false"
          multi-calendars
          @update:model-value="handleDateChange"
      />
    </div>

    <!-- 3) Радиокнопки (Original/Convert) + (Gross/Net) -->
    <div class="flex space-x-6 my-4">
      <div>
        <label class="mr-4">
          <input
              type="radio"
              value="original"
              v-model="baseMetric"
              class="form-radio text-blue-600"
          />
          Original
        </label>
        <label>
          <input
              type="radio"
              value="convert"
              v-model="baseMetric"
              class="form-radio text-blue-600"
          />
          USD
        </label>
      </div>
      <div>
        <label class="mr-4">
          <input
              type="radio"
              value="gross"
              v-model="feeMetric"
              class="form-radio text-blue-600"
          />
          Gross
        </label>
        <label>
          <input
              type="radio"
              value="net"
              v-model="feeMetric"
              class="form-radio text-blue-600"
          />
          Net
        </label>
      </div>
    </div>

    <!-- 4) Чекбоксы для выборки процессингов + Кнопка Filter -->
    <div class="checkboxes my-5 flex flex-col">
      <div class="flex flex-wrap space-x-4 checkboxes-list">
        <label
            v-for="processing in activeProcessings"
            :key="processing.id"
            class="inline-flex items-center mr-6 mb-2"
        >
          <input
              type="checkbox"
              :value="processing.id"
              v-model="tempSelectedProcessings"
              class="form-checkbox h-5 w-5 text-blue-600"
          />
          <span class="ml-2 text-gray-700 dark:text-gray-300">
            {{ processing.name }}
          </span>
        </label>
      </div>
      <button
          @click="applyFilter"
          :disabled="isLoading"
          class="filter-processing-btn bg-blue-500 text-white px-4 py-2 rounded btn-indigo"
      >
        {{ isLoading ? 'Applying...' : 'Filter' }}
      </button>
    </div>

    <!-- ===== ОДНА ОБЪЕДИНЁННАЯ ТАБЛИЦА ===== -->
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
        <tr class="text-left font-bold">
          <th class="pb-4 pt-6 px-6">Processing</th>
          <!-- Week Total -->
          <th class="pb-4 pt-6 px-6">
            {{ combinedMetricLabel }} (Week)
          </th>
          <!-- Month Total -->
          <th class="pb-4 pt-6 px-6">
            {{ combinedMetricLabel }} (Month)
          </th>
          <!-- По дням -->
          <th
              v-for="date in displayedDates"
              :key="date"
              class="pb-4 pt-6 px-6"
          >
            {{ date }}
          </th>
        </tr>
        </thead>

        <tbody>
        <!-- ОДНА СТРОКА на каждый процессинг из обычных транзакций -->
        <tr
            v-for="(data, processingName) in filteredStatistics"
            :key="processingName"
        >
          <td class="border-t px-6 py-4">
            <a
                :href="'/transactions/?processingId=' + data.id"
                class="flex items-center focus:text-indigo-500"
            >
              {{ processingName }}
            </a>
          </td>

          <!-- Итог за неделю (Week) -->
          <td class="border-t px-6 py-4">
            <!-- Обычное значение -->
            {{ getTotalsWeekSelected(processingName) }}
            <!-- PDF-значение (если есть) + разница в % -->
            <div
                class="text-xs text-gray-500"
                v-if="getTotalsWeekSelectedPdf(processingName) !== '0.00'"
            >
              {{ getTotalsWeekSelectedPdf(processingName) }}
              <span
                  v-if="getPdfPercentDiff(getTotalsWeekSelected(processingName), getTotalsWeekSelectedPdf(processingName))"
                  :class="getPdfPercentDiff(getTotalsWeekSelected(processingName), getTotalsWeekSelectedPdf(processingName)).className"
                  class="ml-1"
              >
                  (
                    {{
                      getPdfPercentDiff(
                        getTotalsWeekSelected(processingName),
                        getTotalsWeekSelectedPdf(processingName)
                      ).text
                    }}
                  )
                </span>
            </div>
          </td>

          <!-- Итог за месяц (Month) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsMonthSelected(processingName) }}
            <div
                class="text-xs text-gray-500"
                v-if="getTotalsMonthSelectedPdf(processingName) !== '0.00'"
            >
              {{ getTotalsMonthSelectedPdf(processingName) }}
              <span
                  v-if="getPdfPercentDiff(getTotalsMonthSelected(processingName), getTotalsMonthSelectedPdf(processingName))"
                  :class="getPdfPercentDiff(getTotalsMonthSelected(processingName), getTotalsMonthSelectedPdf(processingName)).className"
                  class="ml-1"
              >
                  (
                    {{
                      getPdfPercentDiff(
                        getTotalsMonthSelected(processingName),
                        getTotalsMonthSelectedPdf(processingName)
                      ).text
                    }}
                  )
                </span>
            </div>
          </td>

          <!-- По дням -->
          <td
              v-for="date in displayedDates"
              :key="date"
              class="border-t px-6 py-4"
          >
            <!-- Обычное -->
            {{ getDateValueSelected(data, date) }}
            <!-- PDF (если есть) + % -->
            <div
                class="text-xs text-gray-500"
                v-if="getDateValueSelectedPdf(processingName, date) !== '0.00'"
            >
              {{ getDateValueSelectedPdf(processingName, date) }}
              <span
                  v-if="getPdfPercentDiff(getDateValueSelected(data, date), getDateValueSelectedPdf(processingName, date))"
                  :class="getPdfPercentDiff(getDateValueSelected(data, date), getDateValueSelectedPdf(processingName, date)).className"
                  class="ml-1"
              >
                  (
                    {{
                      getPdfPercentDiff(
                        getDateValueSelected(data, date),
                        getDateValueSelectedPdf(processingName, date)
                      ).text
                    }}
                  )
                </span>
            </div>
          </td>
        </tr>
        </tbody>

        <tfoot>
        <tr class="font-bold text-left">
          <td class="border-t px-6 py-4">Total</td>

          <!-- Итоговая сумма (Week) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsWeekTotalSelected() }}
            <div
                class="text-xs text-gray-500"
                v-if="getTotalsWeekTotalSelectedPdf() !== '0.00'"
            >
              {{ getTotalsWeekTotalSelectedPdf() }}
              <span
                  v-if="getPdfPercentDiff(getTotalsWeekTotalSelected(), getTotalsWeekTotalSelectedPdf())"
                  :class="getPdfPercentDiff(getTotalsWeekTotalSelected(), getTotalsWeekTotalSelectedPdf()).className"
                  class="ml-1"
              >
                  (
                    {{
                      getPdfPercentDiff(
                        getTotalsWeekTotalSelected(),
                        getTotalsWeekTotalSelectedPdf()
                      ).text
                    }}
                  )
                </span>
            </div>
          </td>

          <!-- Итоговая сумма (Month) -->
          <td class="border-t px-6 py-4">
            {{ getTotalsMonthTotalSelected() }}
            <div
                class="text-xs text-gray-500"
                v-if="getTotalsMonthTotalSelectedPdf() !== '0.00'"
            >
              {{ getTotalsMonthTotalSelectedPdf() }}
              <span
                  v-if="getPdfPercentDiff(getTotalsMonthTotalSelected(), getTotalsMonthTotalSelectedPdf())"
                  :class="getPdfPercentDiff(getTotalsMonthTotalSelected(), getTotalsMonthTotalSelectedPdf()).className"
                  class="ml-1"
              >
                  (
                    {{
                      getPdfPercentDiff(
                        getTotalsMonthTotalSelected(),
                        getTotalsMonthTotalSelectedPdf()
                      ).text
                    }}
                  )
                </span>
            </div>
          </td>

          <!-- Итоги по дням -->
          <td
              v-for="date in displayedDates"
              :key="date"
              class="border-t px-6 py-4"
          >
            {{ getByDayTotalSelected(date) }}
            <div
                class="text-xs text-gray-500"
                v-if="getByDayTotalSelectedPdf(date) !== '0.00'"
            >
              {{ getByDayTotalSelectedPdf(date) }}
              <span
                  v-if="getPdfPercentDiff(getByDayTotalSelected(date), getByDayTotalSelectedPdf(date))"
                  :class="getPdfPercentDiff(getByDayTotalSelected(date), getByDayTotalSelectedPdf(date)).className"
                  class="ml-1"
              >
                  (
                    {{
                      getPdfPercentDiff(
                        getByDayTotalSelected(date),
                        getByDayTotalSelectedPdf(date)
                      ).text
                    }}
                  )
                </span>
            </div>
          </td>
        </tr>
        </tfoot>
      </table>

      <!-- Пример, если нужен ЕДИНЫЙ график (общий) -->
      <div class="my-8 chart-container">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Layout from '@/Shared/Layout'
import Cookies from 'js-cookie'
import VueDatepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  components: {
    Head,
    VueDatepicker
  },
  layout: Layout,

  props: {
    /**
     * ============ ДАННЫЕ ДЛЯ "ОСНОВЫ" (Transaction) ============
     */
    statistics: { type: Object, required: true },

    totalsWeekGrossOriginal: { type: Object, default: () => ({}) },
    totalsWeekNetOriginal: { type: Object, default: () => ({}) },
    totalsWeekGrossConvert: { type: Object, default: () => ({}) },
    totalsWeekNetConvert: { type: Object, default: () => ({}) },

    totalsMonthGrossOriginal: { type: Object, default: () => ({}) },
    totalsMonthNetOriginal: { type: Object, default: () => ({}) },
    totalsMonthGrossConvert: { type: Object, default: () => ({}) },
    totalsMonthNetConvert: { type: Object, default: () => ({}) },

    totalsByDayGrossOriginal: { type: Object, default: () => ({}) },
    totalsByDayNetOriginal: { type: Object, default: () => ({}) },
    totalsByDayGrossConvert: { type: Object, default: () => ({}) },
    totalsByDayNetConvert: { type: Object, default: () => ({}) },

    // Суммарные строки (Week, Month, Day)
    totalsWeekTotalGrossOriginal: { type: String, default: '0.00' },
    totalsWeekTotalNetOriginal: { type: String, default: '0.00' },
    totalsWeekTotalGrossConvert: { type: String, default: '0.00' },
    totalsWeekTotalNetConvert: { type: String, default: '0.00' },

    totalsMonthTotalGrossOriginal: { type: String, default: '0.00' },
    totalsMonthTotalNetOriginal: { type: String, default: '0.00' },
    totalsMonthTotalGrossConvert: { type: String, default: '0.00' },
    totalsMonthTotalNetConvert: { type: String, default: '0.00' },

    totalsDayTotalGrossOriginal: { type: String, default: '0.00' },
    totalsDayTotalNetOriginal: { type: String, default: '0.00' },
    totalsDayTotalGrossConvert: { type: String, default: '0.00' },
    totalsDayTotalNetConvert: { type: String, default: '0.00' },

    /**
     * ============ ДАННЫЕ ДЛЯ PDF (TransactionPdf) ============
     */
    statisticsPdf: { type: Object, required: true },

    totalsWeekPdfGrossOriginal: { type: Object, default: () => ({}) },
    totalsWeekPdfNetOriginal: { type: Object, default: () => ({}) },
    totalsWeekPdfGrossConvert: { type: Object, default: () => ({}) },
    totalsWeekPdfNetConvert: { type: Object, default: () => ({}) },

    totalsMonthPdfGrossOriginal: { type: Object, default: () => ({}) },
    totalsMonthPdfNetOriginal: { type: Object, default: () => ({}) },
    totalsMonthPdfGrossConvert: { type: Object, default: () => ({}) },
    totalsMonthPdfNetConvert: { type: Object, default: () => ({}) },

    totalsByDayPdfGrossOriginal: { type: Object, default: () => ({}) },
    totalsByDayPdfNetOriginal: { type: Object, default: () => ({}) },
    totalsByDayPdfGrossConvert: { type: Object, default: () => ({}) },
    totalsByDayPdfNetConvert: { type: Object, default: () => ({}) },

    // Суммарные строки (Week, Month, Day) - PDF
    totalsWeekPdfTotalGrossOriginal: { type: String, default: '0.00' },
    totalsWeekPdfTotalNetOriginal: { type: String, default: '0.00' },
    totalsWeekPdfTotalGrossConvert: { type: String, default: '0.00' },
    totalsWeekPdfTotalNetConvert: { type: String, default: '0.00' },

    totalsMonthPdfTotalGrossOriginal: { type: String, default: '0.00' },
    totalsMonthPdfTotalNetOriginal: { type: String, default: '0.00' },
    totalsMonthPdfTotalGrossConvert: { type: String, default: '0.00' },
    totalsMonthPdfTotalNetConvert: { type: String, default: '0.00' },

    totalsDayPdfTotalGrossOriginal: { type: String, default: '0.00' },
    totalsDayPdfTotalNetOriginal: { type: String, default: '0.00' },
    totalsDayPdfTotalGrossConvert: { type: String, default: '0.00' },
    totalsDayPdfTotalNetConvert: { type: String, default: '0.00' },

    /**
     * Общие данные
     */
    activeProcessings: { type: Array, required: true },
    selectedProcessings: { type: Array, default: () => [] }
  },

  data() {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 7)

    return {
      // Период "за неделю" по умолчанию
      date: [startDate, endDate],
      selectedTimeZone: Cookies.get('timeZone') || 'Asia/Shanghai',

      // Metric: original/convert, Fee metric: gross/net
      baseMetric: 'original',
      feeMetric: 'gross',

      chart: null,

      // Выбранные процессинги (текущее состояние) и временное
      currentSelectedProcessings: [],
      tempSelectedProcessings: [],

      isLoading: false
    }
  },

  computed: {
    // Даты (только из "обычных" транзакций)
    displayedDates() {
      const allDates = new Set()
      for (const pName in this.statistics) {
        const pData = this.statistics[pName]
        if (pData.dateCurrencyGrossOriginal) {
          Object.keys(pData.dateCurrencyGrossOriginal).forEach(d => allDates.add(d))
        }
        if (pData.dateCurrencyNetOriginal) {
          Object.keys(pData.dateCurrencyNetOriginal).forEach(d => allDates.add(d))
        }
        if (pData.dateCurrencyGrossConvert) {
          Object.keys(pData.dateCurrencyGrossConvert).forEach(d => allDates.add(d))
        }
        if (pData.dateCurrencyNetConvert) {
          Object.keys(pData.dateCurrencyNetConvert).forEach(d => allDates.add(d))
        }
      }
      return [...allDates].sort().reverse()
    },

    // Фильтруем статистику (обычные транзакции) по выбранным ID
    filteredStatistics() {
      const filtered = {}
      for (const pName in this.statistics) {
        const pData = this.statistics[pName]
        if (this.currentSelectedProcessings.includes(pData.id)) {
          filtered[pName] = pData
        }
      }
      return filtered
    },

    // Общая подпись для колонки (Original/Convert, Gross/Net)
    combinedMetricLabel() {
      const bm = this.baseMetric === 'original' ? 'Original' : 'USD'
      const fm = this.feeMetric === 'gross' ? 'Gross' : 'Net'
      return `${bm} (${fm})`
    },

    hasSelectionChanged() {
      if (this.tempSelectedProcessings.length !== this.currentSelectedProcessings.length) {
        return true
      }
      const sortedTemp = [...this.tempSelectedProcessings].sort()
      const sortedCurrent = [...this.currentSelectedProcessings].sort()
      return !sortedTemp.every((val, idx) => val === sortedCurrent[idx])
    }
  },

  watch: {
    // При изменении «метрик» или статистики — обновляем график
    baseMetric: 'updateChart',
    feeMetric: 'updateChart',
    filteredStatistics: 'updateChart'
  },

  methods: {
    setTimeZone() {
      Cookies.set('timeZone', this.selectedTimeZone, { expires: 365 })
      location.reload()
    },
    handleDateChange(selectedDate) {
      // По умолчанию фильтр не применяется сразу
    },

    applyFilter() {
      if (!this.hasSelectionChanged && !this.hasDateChanged()) {
        return
      }
      this.isLoading = true

      const [start, end] = this.date
      const startDate = start ? start.toISOString().split('T')[0] : null
      const endDate = end ? end.toISOString().split('T')[0] : null

      const params = {
        processingIds: this.tempSelectedProcessings,
        startDate,
        endDate
      }
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key].length === 0) {
          delete params[key]
        }
      })

      // Ваш маршрут может отличаться (напр. / и т.п.)
      Inertia.get('/', params, {
        preserveState: true,
        onSuccess: () => {
          this.currentSelectedProcessings = [...this.tempSelectedProcessings]
          this.currentDate = [...this.date]
        },
        onError: () => {
          console.error('Ошибка при применении фильтра.')
        },
        onFinish: () => {
          this.isLoading = false
        }
      })
    },
    hasDateChanged() {
      const [start, end] = this.date
      const currentStart = this.currentDate ? this.currentDate[0] : null
      const currentEnd = this.currentDate ? this.currentDate[1] : null

      const startChanged = start && currentStart
          ? start.getTime() !== currentStart.getTime()
          : start !== currentStart
      const endChanged = end && currentEnd
          ? end.getTime() !== currentEnd.getTime()
          : end !== currentEnd

      return startChanged || endChanged
    },

    /**
     * =================
     * Методы для обычных (Transaction)
     * =================
     */
    getTotalsWeekSelected(processingName) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsWeekGrossOriginal[processingName] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsWeekNetOriginal[processingName] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsWeekGrossConvert[processingName] || '0.00'
      } else {
        return this.totalsWeekNetConvert[processingName] || '0.00'
      }
    },
    getTotalsMonthSelected(processingName) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsMonthGrossOriginal[processingName] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsMonthNetOriginal[processingName] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsMonthGrossConvert[processingName] || '0.00'
      } else {
        return this.totalsMonthNetConvert[processingName] || '0.00'
      }
    },
    getDateValueSelected(procData, date) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return procData.dateCurrencyGrossOriginal?.[date] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return procData.dateCurrencyNetOriginal?.[date] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return procData.dateCurrencyGrossConvert?.[date] || '0.00'
      } else {
        return procData.dateCurrencyNetConvert?.[date] || '0.00'
      }
    },
    getTotalsWeekTotalSelected() {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsWeekTotalGrossOriginal
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsWeekTotalNetOriginal
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsWeekTotalGrossConvert
      } else {
        return this.totalsWeekTotalNetConvert
      }
    },
    getTotalsMonthTotalSelected() {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsMonthTotalGrossOriginal
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsMonthTotalNetOriginal
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsMonthTotalGrossConvert
      } else {
        return this.totalsMonthTotalNetConvert
      }
    },
    getByDayTotalSelected(date) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsByDayGrossOriginal[date] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsByDayNetOriginal[date] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsByDayGrossConvert[date] || '0.00'
      } else {
        return this.totalsByDayNetConvert[date] || '0.00'
      }
    },

    /**
     * =================
     * Методы для PDF (TransactionPdf)
     * =================
     */
    getTotalsWeekSelectedPdf(processingName) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsWeekPdfGrossOriginal[processingName] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsWeekPdfNetOriginal[processingName] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsWeekPdfGrossConvert[processingName] || '0.00'
      } else {
        return this.totalsWeekPdfNetConvert[processingName] || '0.00'
      }
    },
    getTotalsMonthSelectedPdf(processingName) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsMonthPdfGrossOriginal[processingName] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsMonthPdfNetOriginal[processingName] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsMonthPdfGrossConvert[processingName] || '0.00'
      } else {
        return this.totalsMonthPdfNetConvert[processingName] || '0.00'
      }
    },
    getDateValueSelectedPdf(processingName, date) {
      const pdfData = this.statisticsPdf[processingName]
      if (!pdfData) return '0.00' // Нет данных по этому процессингу в PDF

      const metric = this.baseMetric
      const fee = this.feeMetric

      if (metric === 'original' && fee === 'gross') {
        return pdfData.dateCurrencyGrossOriginal?.[date] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return pdfData.dateCurrencyNetOriginal?.[date] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return pdfData.dateCurrencyGrossConvert?.[date] || '0.00'
      } else {
        return pdfData.dateCurrencyNetConvert?.[date] || '0.00'
      }
    },
    getTotalsWeekTotalSelectedPdf() {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsWeekPdfTotalGrossOriginal
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsWeekPdfTotalNetOriginal
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsWeekPdfTotalGrossConvert
      } else {
        return this.totalsWeekPdfTotalNetConvert
      }
    },
    getTotalsMonthTotalSelectedPdf() {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsMonthPdfTotalGrossOriginal
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsMonthPdfTotalNetOriginal
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsMonthPdfTotalGrossConvert
      } else {
        return this.totalsMonthPdfTotalNetConvert
      }
    },
    getByDayTotalSelectedPdf(date) {
      const metric = this.baseMetric
      const fee = this.feeMetric
      if (metric === 'original' && fee === 'gross') {
        return this.totalsByDayPdfGrossOriginal[date] || '0.00'
      } else if (metric === 'original' && fee === 'net') {
        return this.totalsByDayPdfNetOriginal[date] || '0.00'
      } else if (metric === 'convert' && fee === 'gross') {
        return this.totalsByDayPdfGrossConvert[date] || '0.00'
      } else {
        return this.totalsByDayPdfNetConvert[date] || '0.00'
      }
    },

    /**
     * ============ Разница в процентах (PDF vs обычные) ============
     * mainValueStr, pdfValueStr — строки вида "100.00", "0.00" и т.д.
     */
    getPdfPercentDiff(mainValueStr, pdfValueStr) {
      const mainVal = parseFloat(mainValueStr)
      const pdfVal = parseFloat(pdfValueStr)

      if (!mainVal || mainVal === 0) {
        // Если 0 или NaN — не считаем
        return null
      }
      // Вычисляем
      const diff = ((pdfVal - mainVal) / mainVal) * 100
      const diffText = diff.toFixed(2) + '%'
      let className = 'text-green-500'
      if (diff < 0) {
        className = 'text-red-500'
      }
      return {
        text: (diff > 0 ? '+' : '') + diffText,
        className
      }
    },

    // График
    initializeChart() {
      if (this.chart) {
        this.chart.destroy()
      }
      const ctx = this.$refs.chartCanvas.getContext('2d')

      const processingNames = Object.keys(this.filteredStatistics)
      const colors = this.generateColors(processingNames.length)

      const datasets = processingNames.map((pName, idx) => {
        const procData = this.filteredStatistics[pName]
        const dataArr = this.displayedDates.map(d => {
          if (this.baseMetric === 'original' && this.feeMetric === 'gross') {
            return parseFloat(procData.dateCurrencyGrossOriginal?.[d] || 0)
          } else if (this.baseMetric === 'original' && this.feeMetric === 'net') {
            return parseFloat(procData.dateCurrencyNetOriginal?.[d] || 0)
          } else if (this.baseMetric === 'convert' && this.feeMetric === 'gross') {
            return parseFloat(procData.dateCurrencyGrossConvert?.[d] || 0)
          } else {
            return parseFloat(procData.dateCurrencyNetConvert?.[d] || 0)
          }
        })
        return {
          label: pName,
          data: dataArr,
          backgroundColor: colors[idx],
          borderColor: colors[idx],
          fill: false,
          tension: 0.1
        }
      })

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.displayedDates,
          datasets
        },
        options: {
          scales: {
            y: { beginAtZero: true }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      })
    },
    updateChart() {
      this.$nextTick(() => {
        this.initializeChart()
      })
    },
    generateColors(count) {
      const baseColors = [
        'rgba(75, 192, 192, 0.6)',
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 206, 86, 0.6)',
        'rgba(153, 102, 255, 0.6)',
        'rgba(255, 159, 64, 0.6)'
      ]
      const arr = []
      for (let i = 0; i < count; i++) {
        arr.push(baseColors[i % baseColors.length])
      }
      return arr
    }
  },

  mounted() {
    // Инициализируем выбранные процессинги
    if (this.selectedProcessings.length > 0) {
      this.currentSelectedProcessings = [...this.selectedProcessings]
    } else {
      this.currentSelectedProcessings = this.activeProcessings.map(p => p.id)
    }
    this.tempSelectedProcessings = [...this.currentSelectedProcessings]
    this.currentDate = [...this.date]

    // Инициализация графика
    this.$nextTick(() => {
      this.initializeChart()
    })
  },

  unmounted() {
    if (this.chart) {
      this.chart.destroy()
    }
  }
}
</script>

<style scoped>
.date-picker {
  max-width: 450px;
  margin-bottom: 10px;
}
.chart-container {
  width: 100%;
  max-width: 1000px;
  margin: 25px auto;
  height: 400px; /* высота контейнера для графика */
}
.my-4 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.checkboxes {
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
}
button:disabled {
  cursor: not-allowed;
}
.filter-processing-btn {
  max-width: 100px;
  margin: 10px auto 0;
}
.checkboxes-list {
  margin: 10px auto 0;
}
.text-xs {
  font-size: 0.75rem;
}
</style>
